import { z } from 'zod';
import type {
    AnyType,
    ApiExecuteAsyncMetricQueryResults,
    ApiSuccess,
    ApiSuccessEmpty,
    CacheMetadata,
    ItemsMap,
} from '../..';
import { type AiMetricQuery, type AiResultType } from './types';

export * from './constants';
export * from './filterExploreByTags';
export * from './followUpTools';
export * from './requestTypes';
export * from './schemas';
export * from './types';
export * from './utils';

export const baseAgentSchema = z.object({
    uuid: z.string(),
    projectUuid: z.string(),
    organizationUuid: z.string(),

    name: z.string(),
    description: z.string(),
    imageUrl: z.string().url().nullable(),

    tags: z.array(z.string()).nullable(),

    integrations: z.array(
        // z.union([
        // TODO: once we add more integrations, we should use union
        z.object({
            type: z.literal('slack'),
            channelId: z.string(),
        }),
        // ]),
    ),

    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),

    instruction: z
        .string()
        .max(
            8192, // 8kb
            'Custom instruction is too long. Maximum allowed is 8,100 characters.',
        )
        .nullable(),
    provider: z.string(),
    model: z.string(),
    groupAccess: z.array(z.string()),
});

export type BaseAiAgent = z.infer<typeof baseAgentSchema>;

export type AiAgent = Pick<
    BaseAiAgent,
    | 'uuid'
    | 'projectUuid'
    | 'organizationUuid'
    | 'integrations'
    | 'tags'
    | 'name'
    | 'createdAt'
    | 'updatedAt'
    | 'instruction'
    | 'imageUrl'
    | 'groupAccess'
>;

export type AiAgentSummary = Pick<
    AiAgent,
    | 'uuid'
    | 'name'
    | 'integrations'
    | 'tags'
    | 'projectUuid'
    | 'organizationUuid'
    | 'createdAt'
    | 'updatedAt'
    | 'instruction'
    | 'imageUrl'
    | 'groupAccess'
>;

export type AiAgentUser = {
    uuid: string;
    name: string;
};

export type AiAgentMessageUser<TUser extends AiAgentUser = AiAgentUser> = {
    role: 'user';
    uuid: string;
    threadUuid: string;
    message: string; // ai_prompt.prompt
    createdAt: string;

    user: TUser;
};

export type AiAgentMessageAssistant = {
    role: 'assistant';
    uuid: string;
    threadUuid: string;

    // ai_prompt.response
    message: string | null;
    // ai_prompt.responded_at but this can not be null because
    // we check for null before creating the agent message
    createdAt: string;

    vizConfigOutput: object | null;
    filtersOutput: object | null;
    metricQuery: object | null;
    humanScore: number | null;

    toolCalls: AiAgentToolCall[];
    savedQueryUuid: string | null;
};

export type AiAgentMessage<TUser extends AiAgentUser = AiAgentUser> =
    | AiAgentMessageUser<TUser>
    | AiAgentMessageAssistant;

export type AiAgentThreadSummary<TUser extends AiAgentUser = AiAgentUser> = {
    uuid: string;
    agentUuid: string;
    createdAt: string;
    createdFrom: string;
    firstMessage: {
        uuid: string;
        message: string;
    };
    user: TUser;
};

export type AiAgentThread<TUser extends AiAgentUser = AiAgentUser> =
    AiAgentThreadSummary<TUser> & {
        messages: AiAgentMessage<TUser>[];
    };

export type ApiAiAgentResponse = {
    status: 'ok';
    results: AiAgent;
};

export type ApiAiAgentSummaryResponse = {
    status: 'ok';
    results: AiAgentSummary[];
};

export type ApiCreateAiAgent = Pick<
    AiAgent,
    | 'projectUuid'
    | 'integrations'
    | 'tags'
    | 'name'
    | 'instruction'
    | 'imageUrl'
    | 'groupAccess'
>;

export type ApiUpdateAiAgent = Partial<
    Pick<
        AiAgent,
        | 'projectUuid'
        | 'integrations'
        | 'tags'
        | 'name'
        | 'instruction'
        | 'imageUrl'
        | 'groupAccess'
    >
> & {
    uuid: string;
};

export type ApiCreateAiAgentResponse = {
    status: 'ok';
    results: AiAgent;
};

export type ApiAiAgentThreadSummaryListResponse = {
    status: 'ok';
    results: AiAgentThreadSummary[];
};

export type ApiAiAgentThreadResponse = {
    status: 'ok';
    results: AiAgentThread;
};

export type ApiAiAgentThreadCreateRequest = {
    prompt?: string;
};

export type ApiAiAgentThreadCreateResponse = ApiSuccess<AiAgentThreadSummary>;

export type ApiAiAgentThreadMessageCreateRequest = {
    prompt: string;
};

export type ApiAiAgentThreadMessageCreateResponse = ApiSuccess<
    AiAgentMessageUser<AiAgentUser>
>;

export type ApiAiAgentStartThreadResponse = {
    status: 'ok';
    results: {
        jobId: string;
        threadUuid: string;
    };
};

export type ApiAiAgentThreadMessageViz = {
    type: AiResultType;
    metricQuery: AiMetricQuery;
    chartOptions?: object;
    results: {
        rows: Record<string, AnyType>[];
        cacheMetadata: CacheMetadata;
        fields: ItemsMap;
    };
};

export type ApiAiAgentThreadMessageVizResponse = {
    status: 'ok';
    results: ApiAiAgentThreadMessageViz;
};

export type AiVizMetadata = {
    title: string | null;
    description: string | null;
};

export type ApiAiAgentThreadMessageVizQuery = {
    type: AiResultType;
    query: ApiExecuteAsyncMetricQueryResults;
    metadata: AiVizMetadata;
};

export type ApiAiAgentThreadMessageVizQueryResponse = {
    status: 'ok';
    results: ApiAiAgentThreadMessageVizQuery;
};

export type AiAgentUserPreferences = {
    defaultAgentUuid: AiAgent['uuid'];
};

export type ApiGetUserAgentPreferencesResponse =
    | ApiSuccess<AiAgentUserPreferences>
    | ApiSuccessEmpty;

export type ApiUpdateUserAgentPreferences = AiAgentUserPreferences;

export type ApiUpdateUserAgentPreferencesResponse = ApiSuccessEmpty;

export type AiAgentToolCall = {
    uuid: string;
    promptUuid: string;
    toolCallId: string;
    createdAt: Date;
    // TODO: tsoa does not support zod infer schemas - https://github.com/lukeautry/tsoa/issues/1256
    toolName: string; // ToolName zod enum
    toolArgs: object;
};
